import type * as React from 'react'
import { useId, useState } from 'react'
import type { JSX } from 'react'

import clsx from 'clsx'

import {
  AlertDialog,
  Button,
  Flex,
  Form,
  Input,
  Label,
  Paragraph,
  Text,
  toast,
} from '@buffer-mono/popcorn'

import { useIsMobileBreakpoint } from '~publish/hooks/useIsMobileBreakpoint'
import { useAppDispatch } from '~publish/legacy/store'
import { usePostCounts } from '~publish/pages/AllChannels/PostList/usePostCounts'
import { useSelectedChannel } from '~publish/pages/Channel/ChannelContext'

import commonStyles from '../../../GeneralSettings.module.css'
import styles from '../QueueActions.module.css'
import { deleteDrafts } from '../../../thunks/deleteDrafts'

const AlertDialogContent = (args: {
  hideModal: () => void
  channel: ReturnType<typeof useSelectedChannel>
}): JSX.Element => {
  const { hideModal, channel } = args
  const confirmationInputId = useId()
  const { counts, loading } = usePostCounts({
    organizationId: channel.organizationId,
    channelIds: [channel.id],
  })
  const dispatch = useAppDispatch()
  const [isLoading, setLoading] = useState(false)
  const [confirmationText, setConfirmationText] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  if (loading || !counts) return <></>

  const onConfirmDeleteDrafts = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault()
    // Check if the confirmation text is correct
    if (!confirmationText || confirmationText !== 'Delete') {
      setError(`Please type 'Delete' to confirm.`)
      return
    }

    const profileId = channel.id
    setLoading(true)

    const result = await dispatch(deleteDrafts({ profileId }))

    if (deleteDrafts.fulfilled.match(result)) {
      toast.success(result.payload.message)
    } else {
      toast.error(
        `Sorry! Something went wrong while deleting your drafts: ${result.error.message}. Would you be up for trying again?`,
      )
    }

    setLoading(false)
    setConfirmationText('')
    setError(null)
    hideModal()
  }

  const onConfirmationTextChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setConfirmationText(e.target.value)
    setError(null)
  }

  return (
    <AlertDialog.Content>
      <AlertDialog.Title>Delete All Drafts?</AlertDialog.Title>
      <AlertDialog.Description>
        <strong>{counts.drafts}</strong> draft
        {counts.drafts === 1 ? '' : 's'} in your <strong>{channel.name}</strong>{' '}
        channel will be permanently deleted. Posts pending approval will not be
        affected. This action cannot be undone.
      </AlertDialog.Description>
      <AlertDialog.Body>
        <Form
          onSubmit={onConfirmDeleteDrafts}
          id="delete-drafts-confirmation-form"
        >
          <Form.Field name="deleteDraftsConfirmation">
            <Label htmlFor={confirmationInputId}>
              Please type &apos;Delete&apos; to proceed:
            </Label>
            <Input
              id={confirmationInputId}
              onChange={onConfirmationTextChange}
              value={confirmationText}
              aria-required
              aria-describedby={
                error ? `${confirmationInputId}:error` : undefined
              }
              aria-invalid={!!error}
            />
            {error && (
              <Text
                as="span"
                color="critical"
                size="sm"
                role="alert"
                id={`${confirmationInputId}:error`}
              >
                {error}
              </Text>
            )}
          </Form.Field>
        </Form>
      </AlertDialog.Body>
      <AlertDialog.Actions>
        <AlertDialog.Cancel>
          <Button type="button" onClick={hideModal} variant="tertiary">
            Cancel
          </Button>
        </AlertDialog.Cancel>
        <AlertDialog.Action>
          <Button
            type="submit"
            form="delete-drafts-confirmation-form"
            loading={isLoading}
            variant="critical"
            data-testid="delete-drafts-confirm"
          >
            Delete Drafts
          </Button>
        </AlertDialog.Action>
      </AlertDialog.Actions>
    </AlertDialog.Content>
  )
}

export const DeleteDraftsAction = (): JSX.Element => {
  const channel = useSelectedChannel()
  const [isModalShowing, setModalShowing] = useState(false)
  const isMobile = useIsMobileBreakpoint()

  const showModal = (): void => setModalShowing(true)
  const hideModal = (): void => {
    setModalShowing(false)
  }

  // Do not show Delete Drafts action if there is no profile loaded
  if (!channel) return <></>

  return (
    <>
      <Flex
        justify="between"
        className={commonStyles.fullWidth}
        gap="xl"
        align="center"
      >
        <Flex direction="column">
          <Label>Delete All Drafts</Label>
          <Paragraph className={commonStyles.settingsParagraph}>
            Remove all drafts from your {channel.name} channel. Posts pending
            approval will not be affected.
          </Paragraph>
        </Flex>
        <Flex
          className={clsx(commonStyles.actionsWrapper, styles.actionsWrapper)}
        >
          <Button
            size="large"
            variant="critical"
            data-testid="delete-drafts-trigger"
            onClick={showModal}
            className={clsx(commonStyles.actionButton, styles.actionButton)}
          >
            {isMobile ? 'Delete' : 'Delete Drafts'}
          </Button>
        </Flex>
      </Flex>
      <AlertDialog open={isModalShowing}>
        <AlertDialogContent hideModal={hideModal} channel={channel} />
      </AlertDialog>
    </>
  )
}
